import Box from '@mui/material/Box'

/**
 * The environment label, or empty for a live deployment.
 *
 * <p>Driven by <code>VITE_ENV_LABEL</code> at build time rather than hardcoded, so going
 * live is a rebuild with the variable unset — not a code change, a review and a release
 * to remove a warning somebody would otherwise forget about.</p>
 */
export const ENV_LABEL = (import.meta.env.VITE_ENV_LABEL ?? '').trim()

/**
 * How far everything else has to move down, in pixels.
 *
 * <p>Exported because the banner is fixed to the viewport: the app bar and the drawer
 * are fixed too, so they do not flow around it and have to be offset by hand. Zero when
 * there is no banner, which makes the offsets vanish rather than leaving a gap.</p>
 */
export const ENV_BANNER_HEIGHT = ENV_LABEL ? 34 : 0

/** Enough repeats to fill a wide screen before the loop point. */
const REPEATS = 8

/**
 * A scrolling warning strip across the top of every screen.
 *
 * <p>The danger this guards against is specific: a site engineer entering a real day's
 * work into a test system and believing it was recorded. A static chip in the corner is
 * read once on the first day and never seen again, which is why this moves and why it
 * spans the full width.</p>
 *
 * <p>Two copies of the text scroll as one track, translating by exactly half its width,
 * so the loop has no visible seam. Only the first copy is exposed to assistive
 * technology — the rest is decoration, and a screen reader announcing the same sentence
 * eight times is worse than not announcing it at all.</p>
 */
export function EnvBanner() {
  if (!ENV_LABEL) {
    return null
  }

  const run = Array.from({ length: REPEATS }, () => ENV_LABEL).join('  •  ')

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: `${ENV_BANNER_HEIGHT}px`,
        // Above the drawer, which is itself above the app bar, so the strip is never
        // covered by a navigation panel sliding out over it.
        zIndex: (theme) => theme.zIndex.drawer + 2,
        bgcolor: '#c62828',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        borderBottom: '1px solid #8e0000',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          flexShrink: 0,
          minWidth: '200%',
          // Keyframes declared on the element that uses them, which is the pattern
          // Emotion is documented to hoist correctly.
          '@keyframes sems-marquee': {
            from: { transform: 'translateX(0)' },
            to: { transform: 'translateX(-50%)' },
          },
          animation: 'sems-marquee 30s linear infinite',
          // Scrolling text is a genuine accessibility problem for some readers. Honour
          // the setting the operating system already provides: the warning still shows,
          // it simply stops moving.
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            minWidth: 0,
            justifyContent: 'center',
            width: '100%',
          },
        }}
      >
        {[0, 1].map((copy) => (
          <Box
            key={copy}
            aria-hidden={copy === 1 ? true : undefined}
            sx={{
              width: '50%',
              flexShrink: 0,
              textTransform: 'uppercase',
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: '0.09em',
              '@media (prefers-reduced-motion: reduce)': {
                width: 'auto',
                // One copy is plenty when nothing is moving.
                display: copy === 1 ? 'none' : 'block',
              },
            }}
          >
            {run}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
